import React from 'react';
import { EventPageType } from '@graphql/generated';

import { CoupleStory } from '../../../../widgets/CoupleStory';
import { FaqList } from '../../../../widgets/FaqList';
import { ScheduleList } from '../../../../widgets/ScheduleList';
import { TravelList } from '../../../../widgets/TravelList';
import { WeddingPartyList } from '../../../../widgets/WeddingPartyList';
import { LayoutBrannanProps } from '../../LayoutBrannan';
import { Box } from '@withjoy/joykit';
import { EventLogisticsBlock } from '../../../../components/EventLogisticsBlock';
import { RegistryList } from '@apps/guest/packages/layout-engine/widgets/RegistryList';
import { CtaButtons } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons';
import Apps from '@apps/guest/packages/layout-engine/widgets/Apps';
import Custom from '@apps/guest/packages/layout-engine/widgets/Custom';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { button2ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useEventInfo } from '@shared/utils/eventInfo';

export type PreparedPages =
  | EventPageType.app
  | EventPageType.registry
  | EventPageType.travel
  | EventPageType.schedule
  | EventPageType.faq
  | EventPageType.vip
  | EventPageType.story
  | EventPageType.welcome
  | EventPageType.custom;

export const pageWidgets: Readonly<Record<PreparedPages, (props: LayoutBrannanProps) => React.ReactElement>> = {
  [EventPageType.custom]: props => (
    <Box
      data-testid={props.isWelcomePage ? 'page-custom-welcome' : 'page-custom'}
      display={'flex'}
      flexDirection={'column'}
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <Custom
        pageId={props.pageId || ''}
        eventId={props.event.id}
        pageSlug={props.pageSlug || ''}
        pageTitle={props.pageTitle}
        isBrannanLayout
        subTitle={props.subTitle}
        eventHandle={props.eventHandle}
        pages={props.event.pages}
      />
    </Box>
  ),
  [EventPageType.app]: () => (
    <Box
      data-testid={'page-apps'}
      width="100%"
    >
      <Apps data-testid={'page-app'} />
    </Box>
  ),
  [EventPageType.faq]: props => (
    <FaqList
      data-testid={'page-faq'}
      eventHandle={props.eventHandle}
    />
  ),
  [EventPageType.registry]: props => (
    <RegistryList
      eventId={props.event.id}
      eventHandle={props.eventHandle}
    />
  ),
  [EventPageType.schedule]: props => (
    <ScheduleList
      data-testid={'page-schedule'}
      eventHandle={props.eventHandle}
      variant={'inline'}
      eventId={props.event.id}
    />
  ),
  [EventPageType.story]: props => (
    <CoupleStory
      data-testid={'page-story'}
      eventHandle={props.eventHandle}
      eventPages={props.event.pages}
    />
  ),
  [EventPageType.travel]: props => (
    <TravelList
      data-testid={'page-travel'}
      eventHandle={props.eventHandle}
      eventId={props.event.id}
      useMiniNav={false}
      pages={props.event?.pages}
    />
  ),
  [EventPageType.vip]: props => (
    <WeddingPartyList
      data-testid={'page-vip'}
      graphicAccent={props.event.eventDesign?.accent}
      eventId={props.event.id}
    />
  ),
  [EventPageType.welcome]: props => {
    const { eventInfo } = useEventInfo();
    return (
      <Box data-testid={'page-welcome'}>
        <EventLogisticsBlock
          location={props.event.info.location}
          date={props.event.info.finalizedDate}
          hideEventCountdown={eventInfo?.settings?.hideEventCountdown ?? false}
          pages={props.event.pages}
        >
          <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
            <CtaButtons />
          </GuestSiteTypographyOverride>
        </EventLogisticsBlock>
      </Box>
    );
  },
};
